import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { StaffService } from "@/api/staff-members";
import { StaffMembersRepository } from "@/repositories/staff-members";
import type {
  CreateStaffMemberRequestData,
  StaffMember,
  StaffMembersParams,
  StaffMembersResponse,
  UpdateStaffMemberRequestData,
} from "@/api/staff-members/types";
import { queryKeys } from "./constants";

interface UseStaffMembersOptions extends StaffMembersParams {
  enabled?: boolean;
}

export const useStaffMembers = (options: UseStaffMembersOptions = {}) => {
  const { enabled = true, ...params } = options;

  return useQuery<StaffMembersResponse, Error>({
    queryKey: queryKeys.staff.list(params),
    queryFn: () => StaffMembersRepository.instance.getStaffMembers(params),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseStaffMemberOptions {
  enabled?: boolean;
}

export const staffMemberQueryOptions = (id: number) =>
  queryOptions<StaffMember, Error>({
    queryKey: queryKeys.staff.detail(id),
    queryFn: () => StaffService.instance.getStaffMember(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useStaffMember = (
  id: number,
  options: UseStaffMemberOptions = {},
) => {
  const { enabled = true } = options;

  return useQuery({
    ...staffMemberQueryOptions(id),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useCreateStaffMember = () => {
  const queryClient = useQueryClient();

  return useMutation<StaffMember, Error, CreateStaffMemberRequestData>({
    mutationFn: (data) =>
      StaffMembersRepository.instance.createStaffMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
    },
  });
};

export const useUpdateStaffMember = () => {
  const queryClient = useQueryClient();

  return useMutation<
    StaffMember,
    Error,
    { id: number; data: UpdateStaffMemberRequestData }
  >({
    mutationFn: ({ id, data }) =>
      StaffMembersRepository.instance.updateStaffMember(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.staff.detail(variables.id),
      });
    },
    onSuccess: (updatedStaffMember, variables) => {
      queryClient.setQueryData(
        queryKeys.staff.detail(variables.id),
        updatedStaffMember,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
    },
  });
};

export const useResendStaffConfirmation = () =>
  useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => StaffService.instance.resendConfirmation(id),
  });

export const useDeleteStaffMember = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => StaffService.instance.deleteStaffMember(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
      queryClient.removeQueries({
        queryKey: queryKeys.staff.detail(variables.id),
      });
    },
  });
};
